import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import {
  PublicClientApplication,
  AuthenticationResult,
  Configuration,
  AccountInfo,
} from "@azure/msal-browser";
import { msalLoggerCallback, MSAL_LOG_LEVEL } from "@/utils/msalLogger";
import { MsalProvider } from "@azure/msal-react";
import axios from "axios";
import { describeError } from "@/utils/describeError";
import { msalError } from "@/utils/msalError";

interface User {
  id: string;
  email: string;
  name?: string;
  role: "User" | "Administrator";
}

export interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  getAccessToken: () => string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// API URL with fallback for Vite proxy
const API_URL = import.meta.env.VITE_API_URL || "";

// MSAL configuration will be fetched from backend
let msalInstance: PublicClientApplication | null = null;

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps): JSX.Element {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [msalReady, setMsalReady] = useState(false);
  const [jwtToken, setJwtToken] = useState<string | null>(null);

  // Initialize MSAL with configuration from backend
  useEffect(() => {
    const initMsal = async () => {
      try {
        // Fetch Azure Entra ID configuration from backend
        const response = await axios.get(`${API_URL}/api/auth/config`);
        const { tenantId, clientId } = response.data.data;

        // Use /login as redirect URI so we land on the login page (not protected) after auth
        // This prevents ProtectedRoute from redirecting and losing the auth hash
        const redirectUri = `${window.location.origin}/login`;

        const msalConfig: Configuration = {
          auth: {
            clientId: clientId,
            authority: `https://login.microsoftonline.com/${tenantId}`,
            redirectUri: redirectUri,
            navigateToLoginRequestUrl: false, // Don't navigate back to login page after redirect
          },
          cache: {
            cacheLocation: "localStorage",
            storeAuthStateInCookie: true, // Enable for IE11/Edge compatibility and redirect flow
          },
          system: {
            loggerOptions: {
              loggerCallback: msalLoggerCallback,
              // Warning. Verbose was shipping MSAL's internal tracing to every
              // user's console. To get it back while debugging, raise this AND
              // widen the switch in msalLogger — this alone changes nothing.
              logLevel: MSAL_LOG_LEVEL.Warning,
            },
          },
        };

        msalInstance = new PublicClientApplication(msalConfig);
        await msalInstance.initialize();

        // Handle redirect response (for redirect login flow)
        // This MUST be called on every page load to handle the redirect response
        try {
          const redirectResponse = await msalInstance.handleRedirectPromise();

          if (redirectResponse) {
            msalInstance.setActiveAccount(redirectResponse.account);
            await handleAuthentication(redirectResponse.account!);
            sessionStorage.removeItem("msal_login_in_progress");
            setMsalReady(true);
            return;
          }
        } catch (redirectError) {
          console.error("Error handling redirect:", describeError(redirectError));
          sessionStorage.removeItem("msal_login_in_progress");
          setIsLoading(false);
          setMsalReady(true);
          return;
        }

        setMsalReady(true);

        // Check if user is already authenticated
        const accounts = msalInstance.getAllAccounts();
        if (accounts.length > 0) {
          msalInstance.setActiveAccount(accounts[0]);
          await handleAuthentication(accounts[0]);
        } else {
          setIsLoading(false);
        }
      } catch (error) {
        console.error("Failed to initialize MSAL:", describeError(error));
        setIsLoading(false);
        setMsalReady(true);
      }
    };

    initMsal();
  }, []);

  // Handle authentication after MSAL login
  const handleAuthentication = async (account: AccountInfo) => {
    try {
      if (!msalInstance) return;

      // Get tokens from MSAL
      const tokenRequest = {
        scopes: ["User.Read"],
        account: account,
      };

      const response: AuthenticationResult = await msalInstance.acquireTokenSilent(tokenRequest);

      // Exchange Azure AD ID token for our JWT
      // We use idToken instead of accessToken because:
      // - accessToken is for calling Microsoft Graph API and is opaque
      // - idToken is a JWT containing user claims (oid, preferred_username, name, etc.)
      const jwtResponse = await axios.post(`${API_URL}/api/auth/token`, {
        idToken: response.idToken,
      });

      const { token, user: userData } = jwtResponse.data.data;
      setJwtToken(token);
      setUser(userData);
      setIsAuthenticated(true);

      // Store token in localStorage for API requests
      localStorage.setItem("jwt_token", token);

      // Redirect to home page after successful authentication
      if (window.location.pathname === "/login") {
        window.location.href = "/";
      }
    } catch (error: unknown) {
      console.error("Authentication failed:", describeError(error));

      setUser(null);
      setIsAuthenticated(false);
      throw error; // Re-throw so the login function can handle it
    } finally {
      setIsLoading(false);
    }
  };

  // Login function
  const login = async () => {
    if (!msalInstance) {
      throw new Error("MSAL not initialized");
    }

    try {
      setIsLoading(true);
      const loginRequest = {
        scopes: ["User.Read"],
      };

      // In production, use redirect flow directly as popups are often blocked
      // In development, try popup first for better UX
      const isProduction = window.location.hostname !== "localhost";

      if (isProduction) {
        sessionStorage.setItem("msal_login_in_progress", "true");
        await msalInstance.loginRedirect(loginRequest);
        return; // Will redirect, so no need to continue
      }

      // Development: Try popup first, fall back to redirect if popup fails
      try {
        const response: AuthenticationResult = await msalInstance.loginPopup(loginRequest);
        msalInstance.setActiveAccount(response.account);
        await handleAuthentication(response.account!);
      } catch (popupError: unknown) {
        const { errorCode, errorMessage } = msalError(popupError);
        console.error("Popup login failed:", errorCode, errorMessage);

        // If popup was blocked or cancelled, try redirect flow
        if (
          errorCode === "popup_window_error" ||
          errorCode === "user_cancelled" ||
          errorCode === "empty_window_error"
        ) {
          // Store that we're in the middle of a redirect login
          sessionStorage.setItem("msal_login_in_progress", "true");
          await msalInstance.loginRedirect(loginRequest);
          return; // Will redirect, so no need to continue
        }
        throw popupError;
      }
    } catch (error: unknown) {
      // Enhanced error logging for SSO debugging
      console.error("Login failed:", describeError(error));

      // Extract detailed error information from MSAL errors
      const { errorCode, errorMessage, message, name } = msalError(error);
      console.error("MSAL Error Details:", {
        errorCode,
        errorMessage: errorMessage || message,
        errorName: name,
      });

      setIsLoading(false);
      throw error;
    }
  };

  // Logout function
  const logout = async () => {
    if (!msalInstance) return;

    try {
      setIsLoading(true);
      localStorage.removeItem("jwt_token");
      setUser(null);
      setIsAuthenticated(false);
      setJwtToken(null);

      await msalInstance.logoutPopup();
    } catch (error) {
      console.error("Logout failed:", describeError(error));
    } finally {
      setIsLoading(false);
    }
  };

  // Get access token for API requests
  const getAccessToken = () => {
    return jwtToken || localStorage.getItem("jwt_token");
  };

  const value: AuthContextType = {
    user,
    isAuthenticated,
    isLoading,
    login,
    logout,
    getAccessToken,
  };

  // Wait for MSAL to be ready before rendering children
  if (!msalReady) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Initializing authentication...</p>
        </div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={value}>
      <MsalProvider instance={msalInstance!}>{children}</MsalProvider>
    </AuthContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components -- a context file exports its provider and its hook together, which is the standard React pattern; the cost is that this file does not hot-reload
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
